import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, ShieldQuestion, CheckCircle2, Clock } from "lucide-react";

export default function CrimeDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(undefined);
  useEffect(() => { setS(undefined); api.get(`/public/crime/${slug}`).then((r) => setS(r.data)).catch(() => setS(null)); }, [slug]);

  if (s === undefined) return <div className="max-w-3xl mx-auto px-4 py-24 text-ink/55">Loading…</div>;
  if (s === null) return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="font-editorial text-3xl font-bold text-ink">Story not found</h1><Link to="/global-crime-watch" className="mt-4 inline-block text-heritage underline">Back to Crime Watch</Link></div>;

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <title>{`${s.headline} | Global Crime Watch`}</title>
      <meta name="description" content={s.summary} />
      <div className="flex items-center gap-3 flex-wrap">
        <span className="label-caps text-clay">Global Crime Watch</span>
        <span className="label-caps text-ink/55">/ {s.crime_category}</span>
      </div>
      <h1 data-testid="crime-headline" className="mt-4 font-editorial font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-ink">{s.headline}</h1>

      <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-1.5 text-ink" data-testid="legal-status">
          <ShieldQuestion className="h-4 w-4" /> Legal status: <strong>{s.legal_status}</strong>
        </span>
        <span className="text-ink/55">{s.country}{s.city ? ` · ${s.city}` : ""} · {s.geographic_region}</span>
      </div>

      {s.presumption_notice && (
        <div data-testid="presumption-notice" className="mt-5 border-l-4 border-ink bg-mist px-5 py-4 text-sm text-ink">
          {s.presumption_notice}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink/55 border-y border-ink/10 py-4">
        {s.first_published_at && <span>Published {new Date(s.first_published_at).toLocaleDateString()}</span>}
        {s.last_updated_at && s.last_updated_at !== s.first_published_at && <span data-testid="crime-updated">· Updated {new Date(s.last_updated_at).toLocaleDateString()}</span>}
        {s.last_corrected_at && <span className="text-clay font-medium">· Corrected {new Date(s.last_corrected_at).toLocaleDateString()}</span>}
      </div>

      <p className="mt-6 text-lg text-ink/75 leading-relaxed">{s.summary}</p>

      <div className="mt-10 grid gap-6">
        <section className="border border-emerald-200 bg-emerald-50/50 p-5">
          <h2 className="flex items-center gap-2 font-editorial font-bold text-lg text-emerald-900"><CheckCircle2 className="h-5 w-5" /> Confirmed</h2>
          <div className="prose-editorial mt-3 text-ink/80" dangerouslySetInnerHTML={{ __html: s.confirmed_facts || "<p>—</p>" }} />
        </section>
        {s.allegations && (
          <section className="border border-amber-200 bg-amber-50/50 p-5">
            <h2 className="flex items-center gap-2 font-editorial font-bold text-lg text-amber-900"><AlertTriangle className="h-5 w-5" /> Alleged (unproven)</h2>
            <p className="mt-3 text-ink/80">{s.allegations}</p>
          </section>
        )}
        {s.disputed_facts && (
          <section className="border border-ink/10 bg-white p-5">
            <h2 className="font-editorial font-bold text-lg text-ink">Disputed</h2>
            <p className="mt-3 text-ink/80">{s.disputed_facts}</p>
          </section>
        )}
      </div>

      {s.updates?.length > 0 && (
        <section className="mt-12 border-t-2 border-ink pt-6">
          <h2 className="flex items-center gap-2 font-editorial font-bold text-xl text-ink"><Clock className="h-5 w-5 text-gold" /> Story Updates</h2>
          <ol className="mt-4 space-y-4">
            {s.updates.map((u, i) => (
              <li key={i} className="border-l-2 border-gold/60 pl-4">
                <div className="text-xs text-ink/55">{new Date(u.created_at).toLocaleString()}</div>
                <div className="font-semibold text-ink">{u.headline}</div>
                {u.new_confirmed && <p className="text-sm text-emerald-800">Confirmed: {u.new_confirmed}</p>}
                {u.new_allegation && <p className="text-sm text-amber-800">Alleged: {u.new_allegation}</p>}
                {u.legal_status_change && <p className="text-sm text-ink">Legal status → <strong>{u.legal_status_change}</strong></p>}
              </li>
            ))}
          </ol>
        </section>
      )}

      {s.corrections?.length > 0 && (
        <div data-testid="crime-corrections" className="mt-10 border-l-4 border-clay bg-white px-5 py-4">
          <div className="flex items-center gap-2 font-semibold text-clay"><AlertTriangle className="h-4 w-4" /> Corrections</div>
          <ul className="mt-2 space-y-1 text-sm text-ink/80">
            {s.corrections.map((c, i) => <li key={i}>{new Date(c.corrected_at).toLocaleDateString()}: {c.explanation}</li>)}
          </ul>
        </div>
      )}

      {s.sources?.length > 0 && (
        <section className="mt-12 border-t-2 border-ink pt-6">
          <h2 className="font-editorial font-bold text-xl text-ink">Sources</h2>
          <ol className="mt-4 space-y-2 list-decimal list-inside text-sm">
            {s.sources.map((src, i) => (
              <li key={i} className="text-ink/80">{src.title}{src.publisher && <span className="text-ink/55"> — {src.publisher}</span>}
                {src.url && <a href={src.url} target="_blank" rel="noreferrer" className="ml-2 text-heritage underline underline-offset-2">link</a>}</li>
            ))}
          </ol>
        </section>
      )}
    </article>
  );
}
